import { onBeforeUnmount, onMounted, ref } from "vue";

export const GIFT_ORDER_INSTRUCTION = "Package as a gift";
export const GIFT_ORDER_COMMENT_LIMIT = 1000;

const GIFT_ORDER_STORAGE_KEY = "hooligans:gift-order";
const GIFT_ORDER_CHANGED_EVENT = "hooligans:gift-order-changed";
const GIFT_ORDER_MEMORY_KEY = "__hooligansGiftOrderPreference";
const GIFT_ORDER_SEPARATOR = "\n\n";

type GiftOrderWindow = Window &
  Record<typeof GIFT_ORDER_MEMORY_KEY, boolean | undefined>;

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const hasGiftInstruction = (comment: string) =>
  comment.split(/\r?\n/).some((line) => line.trim() === GIFT_ORDER_INSTRUCTION);

// A property on window is shared by this tab's mounted controls, but is never
// created during SSR. It only backs up the preference when storage is blocked.
const getMemoryPreference = (): boolean | null => {
  if (typeof window === "undefined") return null;

  try {
    const value = (window as GiftOrderWindow)[GIFT_ORDER_MEMORY_KEY];
    return typeof value === "boolean" ? value : null;
  } catch {
    return null;
  }
};

const setMemoryPreference = (isGiftOrder: boolean): void => {
  if (typeof window === "undefined") return;

  try {
    (window as GiftOrderWindow)[GIFT_ORDER_MEMORY_KEY] = isGiftOrder;
  } catch {
    // Some hardened browser contexts can reject writes to window.
  }
};

/**
 * Reads the order-wide gift preference. This deliberately uses session storage
 * so it survives reloads within a tab without becoming a shopper account
 * preference or leaking between SSR requests.
 */
export const getGiftOrderPreference = (): boolean => {
  // A write can fail with QuotaExceededError even though reads still work.
  // This tab's last explicit choice wins over a stale persisted value.
  const memory = getMemoryPreference();
  if (memory !== null) return memory;
  const storage = getSessionStorage();
  try {
    if (storage) {
      return storage.getItem(GIFT_ORDER_STORAGE_KEY) === "1";
    }
  } catch {
    // Fall through to this tab's non-persistent backup.
  }

  return getMemoryPreference() === true;
};

/** Persists an order-wide gift preference without throwing in private modes. */
export const setGiftOrderPreference = (isGiftOrder: boolean): void => {
  setMemoryPreference(isGiftOrder);

  try {
    const storage = getSessionStorage();
    if (isGiftOrder) {
      storage?.setItem(GIFT_ORDER_STORAGE_KEY, "1");
    } else {
      storage?.removeItem(GIFT_ORDER_STORAGE_KEY);
    }
  } catch {
    // Storage can be disabled. The caller still keeps its current UI state.
  }

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(GIFT_ORDER_CHANGED_EVENT));
    } catch {
      // Older/privacy-restricted browser contexts can reject CustomEvent.
    }
  }
};

export const resetGiftOrderPreference = (): void =>
  setGiftOrderPreference(false);

/**
 * Per-component reactive access to the order-wide preference. State is local
 * to each call, which keeps SSR requests isolated; browser listeners keep
 * separately-mounted shopping-bag and checkout controls in sync.
 */
export const useGiftOrderPreference = () => {
  const isGiftOrder = ref(false);

  const sync = () => {
    isGiftOrder.value = getGiftOrderPreference();
  };

  const setIsGiftOrder = (value: boolean) => {
    isGiftOrder.value = Boolean(value);
    setGiftOrderPreference(isGiftOrder.value);
  };

  onMounted(() => {
    sync();
    window.addEventListener(GIFT_ORDER_CHANGED_EVENT, sync);
  });
  onBeforeUnmount(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener(GIFT_ORDER_CHANGED_EVENT, sync);
    }
  });

  return { isGiftOrder, setIsGiftOrder };
};

/**
 * Adds the staff-facing instruction only for the confirmation request. The
 * shopper's editable note is never changed, so turning the preference off
 * cannot erase their wording. Reserve room for the instruction before the
 * backend's 1000-character cap is applied.
 */
export const composeGiftOrderComment = (
  customerComment: unknown,
  isGiftOrder: boolean
): string => {
  const comment = String(customerComment || "").trim();

  if (!isGiftOrder) return comment.slice(0, GIFT_ORDER_COMMENT_LIMIT);
  const cappedComment = comment.slice(0, GIFT_ORDER_COMMENT_LIMIT);
  if (hasGiftInstruction(cappedComment)) {
    return cappedComment;
  }

  if (!comment) return GIFT_ORDER_INSTRUCTION;

  const availableCustomerCharacters =
    GIFT_ORDER_COMMENT_LIMIT -
    GIFT_ORDER_SEPARATOR.length -
    GIFT_ORDER_INSTRUCTION.length;
  return `${comment
    .slice(0, Math.max(0, availableCustomerCharacters))
    .trimEnd()}${GIFT_ORDER_SEPARATOR}${GIFT_ORDER_INSTRUCTION}`;
};
